import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, FolderKanban, FileText, Mail, CreditCard, DollarSign, RefreshCw, Bookmark,
} from "lucide-react";
import DailySignupsPanel from "@/components/DailySignupsPanel";

function StatCard({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`border ${accent ? "border-[#2563EB]/40 bg-[#2563EB]/5" : "border-slate-200 bg-white"} p-5`}
    >
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="overline">{label}</span>
      </div>
      <div className="mt-2 font-serif text-3xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function AdminStatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chuck, setChuck] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/chuck/stats"),
      ]);
      setData(a.data);
      setChuck(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load stats");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-stats-root" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline mb-2 text-[#2563EB]">ADMIN</div>
          <h1 className="font-serif text-4xl font-semibold leading-tight text-slate-900">
            Production stats.
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Live pulse of Qualmitta — signups, projects, drafts, MRR, and Chuck training progress.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-stats-refresh"
          className="inline-flex items-center gap-2 border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="p-8 text-center text-xs text-slate-400">Loading…</div>
      ) : data ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              testid="stat-users"
              icon={Users}
              label="TOTAL USERS"
              value={data.users.total.toLocaleString()}
              sub={`${data.users.last_24h} today · ${data.users.last_7d} this week`}
            />
            <StatCard
              testid="stat-projects"
              icon={FolderKanban}
              label="PROJECTS"
              value={data.projects.total.toLocaleString()}
              sub={`${data.projects.last_24h} created today`}
            />
            <StatCard
              testid="stat-drafts"
              icon={FileText}
              label="DRAFTS GENERATED"
              value={data.drafts.total.toLocaleString()}
              sub={`${data.drafts.last_24h} today`}
            />
            <StatCard
              testid="stat-contacts"
              icon={Mail}
              label="CONTACT MESSAGES"
              value={data.contacts.total.toLocaleString()}
              sub={`${data.contacts.last_24h} today`}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <StatCard
              testid="stat-mrr"
              icon={DollarSign}
              accent
              label="MRR (USD)"
              value={`$${data.paid_subscribers.mrr_usd.toLocaleString()}`}
              sub={`${data.paid_subscribers.total} paid subscribers`}
            />
            <StatCard
              testid="stat-pro"
              icon={CreditCard}
              label="PRO SUBSCRIBERS"
              value={data.paid_subscribers.pro}
              sub="$399 / mo"
            />
            <StatCard
              testid="stat-enterprise"
              icon={CreditCard}
              label="ENTERPRISE SUBSCRIBERS"
              value={data.paid_subscribers.enterprise}
              sub="$799 / mo"
            />
          </div>

          <DailySignupsPanel />

          {chuck && (
            <div className="border border-slate-200 bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="overline text-slate-500">CHUCK · TRAINING DATA</div>
                  <div className="font-serif text-2xl">Consented interactions collected</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-4xl font-semibold text-[#93C5FD]">
                    {chuck.total_events.toLocaleString()}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {chuck.consenting_users} / {chuck.total_users} users consenting ({Math.round((chuck.consent_rate || 0) * 100)}%)
                  </div>
                </div>
              </div>
              {Object.keys(chuck.by_event_type || {}).length > 0 ? (
                <div className="grid gap-2 md:grid-cols-3">
                  {Object.entries(chuck.by_event_type).map(([k, v]) => (
                    <div key={k} className="flex justify-between border border-slate-100 bg-[#FAFAF7] px-3 py-2 text-xs">
                      <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono text-slate-800">{v.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500">
                  No events yet. Chuck starts learning once users opt in and use the platform.
                </div>
              )}
            </div>
          )}

          <div className="border border-slate-200 bg-white">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
              <div className="overline text-slate-500">RECENT SIGNUPS</div>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {(data.recent_signups || []).map((u, i) => (
                <div key={i} className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-sm">
                  <div>
                    <div className="text-slate-800">{u.email}</div>
                    {u.name && <div className="text-xs text-slate-400">{u.name}</div>}
                  </div>
                  <div className="text-right text-xs text-slate-400">
                    {u.created_at?.slice(0, 19).replace("T", " ")}
                    <div className="text-[10px] text-slate-300">{u.auth_method || u.auth_type || "?"}</div>
                  </div>
                </div>
              ))}
              {(data.recent_signups || []).length === 0 && (
                <div className="p-4 text-center text-xs text-slate-400">No signups yet.</div>
              )}
            </div>
          </div>

          <div className="text-center text-[10px] text-slate-300">
            As of {new Date(data.as_of).toLocaleString()} · preview environment only
          </div>
        </>
      ) : null}
    </div>
  );
}
